import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, OverlayView } from '@react-google-maps/api';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Chip, CircularProgress, IconButton,
  InputBase, Button, Tooltip, Dialog, DialogContent, Divider,
  Avatar, Menu, MenuItem, useMediaQuery, Drawer, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CloseIcon from '@mui/icons-material/Close';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapIcon from '@mui/icons-material/Map';
import ListIcon from '@mui/icons-material/List';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import GroupIcon from '@mui/icons-material/Group';
import TimeToLeaveIcon from '@mui/icons-material/TimeToLeave';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import restaurantService from '../../services/restaurant.service';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const GMAPS_LIBS = ['places'];
const MAYOTTE = { lat: -12.7806, lng: 45.2278 };
const CITY_COORDS = {
  mamoudzou: { lat: -12.7806, lng: 45.2278 }, sada: { lat: -12.8476, lng: 45.1193 },
  koungou: { lat: -12.7248, lng: 45.2095 }, bandraboua: { lat: -12.7006, lng: 45.2094 },
  chiconi: { lat: -12.8536, lng: 45.1303 }, tsingoni: { lat: -12.8198, lng: 45.1272 },
  ouangani: { lat: -12.8748, lng: 45.1640 }, dembéni: { lat: -12.8389, lng: 45.1682 },
  chirongui: { lat: -12.9337, lng: 45.1471 }, labattoir: { lat: -12.7771, lng: 45.2582 },
};
function getCoords(r) {
  if (r.latitude && r.longitude) return { lat: parseFloat(r.latitude), lng: parseFloat(r.longitude) };
  if (r.city) {
    const key = r.city.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const found = Object.entries(CITY_COORDS).find(([k]) => k.normalize('NFD').replace(/[̀-ͯ]/g, '') === key);
    if (found) return found[1];
  }
  return null;
}
// Même calcul que la fonction create-order : 2,50 € par tranche de 5 km
// (vol d'oiseau resto → adresse), 2,50 € si une position manque.
function estimateDeliveryFee(r, center) {
  if (!center || !r.latitude || !r.longitude) return 2.5;
  const toRad = d => (d * Math.PI) / 180;
  const dLat = toRad(parseFloat(r.latitude) - center.lat);
  const dLng = toRad(parseFloat(r.longitude) - center.lng);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(center.lat)) * Math.cos(toRad(parseFloat(r.latitude))) * Math.sin(dLng / 2) ** 2;
  const distKm = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.ceil(distKm / 5) * 2.5;
}
const fmtEuro = n => n.toFixed(2).replace('.', ',');
const NAVBAR_H = 64;


/* Marqueur carte : icône seule → nom+note au hover */
function RestaurantMarker({ r, hovered, onEnter, onLeave, onClick }) {
  return (
    <div
      onClick={onClick}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
    >
      {/* Bulle nom au hover */}
      {hovered && (
        <div style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          marginBottom: 6, background: '#1a1a1a', color: '#fff',
          borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 700,
          whiteSpace: 'nowrap', boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          display: 'flex', alignItems: 'center', gap: 4, pointerEvents: 'none',
        }}>
          {r.rating > 0 && <span style={{ fontSize: 10 }}>★</span>}
          <span>{r.name}</span>
          {r.rating > 0 && <span style={{ opacity: 0.7 }}>{Number(r.rating).toFixed(1)}</span>}
        </div>
      )}
      {/* Icône */}
      <div style={{
        width: hovered ? 36 : 28, height: hovered ? 36 : 28,
        background: hovered ? '#1a1a1a' : '#fff',
        border: `2px solid ${hovered ? '#1a1a1a' : '#ccc'}`,
        borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: hovered ? 16 : 13,
        boxShadow: hovered ? '0 4px 16px rgba(0,0,0,0.3)' : '0 1px 6px rgba(0,0,0,0.18)',
        transition: 'all 0.15s',
      }}>
        🍴
      </div>
    </div>
  );
}

const CATEGORIES = [
  { label: 'Pizzéria',       icon: '🍕', value: 'Pizzéria' },
  { label: 'Fast food',      icon: '🍟', value: 'Fast food' },
  { label: 'Burgers',        icon: '🍔', value: 'Burgers' },
  { label: 'Sushis',        icon: '🍱', value: 'Sushis & Japonais' },
  { label: 'Mahoraise',      icon: '🥥', value: 'Cuisine mahoraise' },
  { label: 'Africaine',      icon: '🥘', value: 'Cuisine africaine' },
  { label: 'Créole',         icon: '🍛', value: 'Cuisine créole' },
  { label: 'Française',      icon: '🥐', value: 'Cuisine française' },
  { label: 'Italienne',      icon: '🍝', value: 'Cuisine italienne' },
  { label: 'Asiatique',      icon: '🥡', value: 'Cuisine asiatique' },
  { label: 'Chinoise',       icon: '🥟', value: 'Cuisine chinoise' },
  { label: 'Indienne',       icon: '🍲', value: 'Cuisine indienne' },
  { label: 'Mexicaine',      icon: '🌮', value: 'Cuisine mexicaine' },
  { label: 'Libanaise',      icon: '🧆', value: 'Cuisine libanaise' },
  { label: 'Marocaine',      icon: '🫕', value: 'Cuisine marocaine' },
  { label: 'Pâtisserie',     icon: '🍰', value: 'Pâtisserie & Desserts' },
  { label: 'Boulangerie',    icon: '🥖', value: 'Boulangerie' },
  { label: 'Sandwichs',      icon: '🥪', value: 'Sandwichs & Wraps' },
  { label: 'Healthy',        icon: '🥗', value: 'Salades & Healthy' },
  { label: 'Végétarien',     icon: '🌿', value: 'Végétarien & Vegan' },
  { label: 'Fruits de mer',  icon: '🦞', value: 'Fruits de mer & Poissons' },
  { label: 'Grillades',      icon: '🥩', value: 'Grillades & Viandes' },
  { label: 'Snack',          icon: '🍿', value: 'Snack' },
  { label: 'Café & Brunch',  icon: '☕', value: 'Café & Brunch' },
  { label: 'Épicerie',       icon: '🛒', value: 'Épicerie' },
  { label: 'Divers',         icon: '🍽️', value: 'Divers' },
];


/* ── Carte restaurant (style Uber Eats) ─────────────────────────────────── */
function RestaurantCard({ r, fee, hovered, onHover, onLeave }) {
  const [fav, setFav] = useState(false);

  const promo = r.has_promo
    ? r.promo_label || 'Offre disponible'
    : null;

  return (
    <Box
      component={Link}
      to={`/restaurants/${r.slug || r.id}`}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      sx={{
        textDecoration: 'none', display: 'block', borderRadius: '16px',
        overflow: 'hidden', bgcolor: 'white',
        transition: 'box-shadow 0.2s',
        boxShadow: hovered ? '0 4px 20px rgba(0,0,0,0.14)' : '0 1px 4px rgba(0,0,0,0.06)',
        opacity: r.is_open ? 1 : 0.72,
        cursor: 'pointer',
      }}
    >
      {/* Image */}
      <Box sx={{ position: 'relative', height: 180, bgcolor: '#f3f3f3', overflow: 'hidden' }}>
        {r.cover_image ? (
          <Box component="img" src={r.cover_image} alt={r.name}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.35s', '.card:hover &': { transform: 'scale(1.04)' } }} />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, rgba(85,37,131,0.08) 0%, rgba(253,185,39,0.12) 100%)' }}>
            <Typography sx={{ fontSize: 48 }}>🍽️</Typography>
          </Box>
        )}

        {/* Promo banner */}
        {promo && (
          <Box sx={{
            position: 'absolute', top: 12, left: 0,
            bgcolor: '#e86339', color: 'white',
            px: 1.5, py: 0.4,
            fontSize: '0.75rem', fontWeight: 700,
            borderRadius: '0 6px 6px 0',
          }}>
            {promo}
          </Box>
        )}

        {/* Closed overlay */}
        {!r.is_open && (
          <Box sx={{
            position: 'absolute', inset: 0,
            bgcolor: 'rgba(0,0,0,0.42)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Box sx={{ bgcolor: 'rgba(0,0,0,0.65)', color: 'white', borderRadius: '6px', px: 2, py: 0.6, fontSize: '0.8rem', fontWeight: 700 }}>
              Fermé
            </Box>
          </Box>
        )}

        {/* Delivery suspended badge */}
        {r.is_open && r.delivery_enabled === false && (
          <Box sx={{
            position: 'absolute', bottom: 10, left: 10,
            bgcolor: '#f57c00', color: 'white',
            borderRadius: '6px', px: 1.2, py: 0.3,
            fontSize: '0.7rem', fontWeight: 700,
          }}>
            🛵 Livraison indisponible
          </Box>
        )}

        {/* Heart */}
        <IconButton
          size="small"
          onClick={e => { e.preventDefault(); setFav(p => !p); }}
          sx={{
            position: 'absolute', top: 8, right: 8,
            bgcolor: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(4px)',
            width: 32, height: 32,
            '&:hover': { bgcolor: 'white' },
          }}
        >
          {fav
            ? <FavoriteIcon sx={{ fontSize: 17, color: '#e11d48' }} />
            : <FavoriteBorderIcon sx={{ fontSize: 17, color: '#1a1a1a' }} />}
        </IconButton>
      </Box>

      {/* Info */}
      <Box sx={{ px: 1.5, pt: 1.2, pb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.4 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a', lineHeight: 1.2, pr: 1 }} noWrap>
            {r.name}
          </Typography>
          {r.rating > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexShrink: 0 }}>
              <StarIcon sx={{ fontSize: 13, color: '#1a1a1a' }} />
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a1a' }}>
                {Number(r.rating).toFixed(1)}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
          <AccessTimeIcon sx={{ fontSize: 13, color: '#6b6b6b' }} />
          <Typography sx={{ fontSize: '0.78rem', color: '#6b6b6b' }}>
            {r.avg_delivery_time || '—'} min
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#6b6b6b' }}>·</Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#6b6b6b' }}>
            {fee == null ? 'livraison dès 2,50 €' : `livraison ${fmtEuro(fee)} €`}
          </Typography>
        </Box>

        {/* Tag */}
        {r.rating >= 4.3 && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5,
            bgcolor: '#f5f5f5', borderRadius: '4px', px: 1, py: 0.3 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#1a1a1a' }}>
              Bon rapport qualité-prix
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

/* ── Page principale ─────────────────────────────────────────────────────── */
// Commerces alimentaires (page /courses) : épiceries, doukas, supérettes.
const GROCERY_RE = /picerie|douka|sup[eé]rette|alimentation|supermarch/i;
export const isGrocery = (r) => GROCERY_RE.test(r.cuisine_type || '');

export default function RestaurantList({ mode = 'restaurants' }) {
  const courses = mode === 'courses';
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const isMobile = useMediaQuery('(max-width:899px)');
  const [authMenu, setAuthMenu] = useState(null);
  const [mobileDrawer, setMobileDrawer] = useState(false);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCuisine, setSelectedCuisine] = useState(null);
  const [orderType, setOrderType] = useState('delivery');
  const [sortBy, setSortBy] = useState(null); // null | 'rating' | 'time' | 'price'
  const [hoveredId, setHoveredId] = useState(null);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [mapMoved, setMapMoved] = useState(false);
  const [searchCenter, setSearchCenter] = useState(null);
  const mapInstanceRef = useRef(null);
  const catScrollRef = useRef(null);

  const { isLoaded: mapsLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY, libraries: GMAPS_LIBS, language: 'fr',
  });

  const mapOptions = useMemo(() => ({
    zoom: 12, center: MAYOTTE, zoomControl: false, streetViewControl: false,
    mapTypeControl: false, fullscreenControl: false, clickableIcons: false,
    gestureHandling: 'greedy',
    mapId: 'DEMO_MAP_ID',
  }), []);

  const onMapLoad = useCallback((map) => {
    mapInstanceRef.current = map;
    if (!acServiceRef.current && window.google?.maps?.places) {
      acServiceRef.current = new window.google.maps.places.AutocompleteService();
      placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
    }
  }, []);

  const [locationOpen, setLocationOpen] = useState(false);
  const [locationMode, setLocationMode] = useState('main');
  const [locationLabel, setLocationLabel] = useState('Mayotte');
  const [locationSubLabel, setLocationSubLabel] = useState('Mayotte');
  const [deliveryPoint, setDeliveryPoint] = useState(null); // { lat, lng } de l'adresse choisie
  const [locationQuery, setLocationQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [deliveryTime, setDeliveryTime] = useState('Maintenant');
  const acServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const locationDebounce = useRef(null);


  const searchLocation = useCallback((text) => {
    if (!text || text.length < 2 || !acServiceRef.current) { setLocationSuggestions([]); return; }
    setLocationLoading(true);
    acServiceRef.current.getPlacePredictions(
      {
        input: text,
        locationBias: new window.google.maps.LatLngBounds(
          { lat: -13.1, lng: 44.9 }, { lat: -12.6, lng: 45.35 }
        ),
        language: 'fr',
      },
      (preds, status) => {
        setLocationLoading(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && preds) {
          setLocationSuggestions(preds);
        } else setLocationSuggestions([]);
      }
    );
  }, []);

  const handleLocationInput = useCallback((e) => {
    const v = e.target.value;
    setLocationQuery(v);
    clearTimeout(locationDebounce.current);
    locationDebounce.current = setTimeout(() => searchLocation(v), 280);
  }, [searchLocation]);

  const [geoLoading, setGeoLoading] = useState(false);

  const handleGeolocate = useCallback(() => {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const { latitude: lat, longitude: lng } = coords;
        setDeliveryPoint({ lat, lng });
        if (!placesServiceRef.current) {
          setGeoLoading(false);
          setLocationLabel('Ma position');
          setLocationOpen(false);
          return;
        }
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng }, language: 'fr' }, (results, status) => {
          setGeoLoading(false);
          const label = (status === 'OK' && results[0])
            ? (results[0].address_components?.find(c => c.types.includes('route'))?.long_name || results[0].formatted_address)
            : 'Ma position';
          setLocationLabel(label);
          setLocationSubLabel('Ma position');
          setLocationMode('main');
          setLocationQuery(''); setLocationSuggestions([]);
        });
      },
      () => setGeoLoading(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  const handleLocationSelect = useCallback((pred) => {
    if (!placesServiceRef.current) return;
    placesServiceRef.current.getDetails(
      { placeId: pred.place_id, fields: ['geometry', 'name', 'formatted_address', 'address_components'] },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setDeliveryPoint({ lat, lng });
        const main = pred.structured_formatting?.main_text || place.name || pred.description;
        const secondary = pred.structured_formatting?.secondary_text || place.formatted_address || '';
        setLocationLabel(main);
        setLocationSubLabel(secondary);
        setLocationQuery('');
        setLocationSuggestions([]);
        setLocationMode('main');
      }
    );
  }, []);


  /* Load restaurants */
  useEffect(() => {
    restaurantService.getAll()
      .then(data => setRestaurants(data.restaurants || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  /* Filtered list */
  const filtered = restaurants
    .filter(r => {
      if (courses && !isGrocery(r)) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!r.name.toLowerCase().includes(q) && !r.cuisine_type?.toLowerCase().includes(q)) return false;
      }
      if (selectedCuisine && !r.cuisine_type?.toLowerCase().includes(selectedCuisine.toLowerCase()) && r.cuisine_type !== selectedCuisine) return false;
      if (searchCenter && r.latitude && r.longitude) {
        const dlat = parseFloat(r.latitude) - searchCenter.lat;
        const dlng = parseFloat(r.longitude) - searchCenter.lng;
        const distKm = Math.sqrt(dlat * dlat + dlng * dlng) * 111;
        if (distKm > 15) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'time') return (a.avg_delivery_time || 99) - (b.avg_delivery_time || 99);
      if (sortBy === 'price') {
        return (estimateDeliveryFee(a, deliveryPoint) - estimateDeliveryFee(b, deliveryPoint))
          || ((parseFloat(a.min_order_amount) || 0) - (parseFloat(b.min_order_amount) || 0));
      }
      return 0;
    });

  const topSortLabel = sortBy === 'rating' ? 'Mieux notés' : sortBy === 'time' ? 'Plus rapides' : sortBy === 'price' ? 'Moins cher' : 'Trier';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: { xs: 'auto', md: '100vh' }, overflow: { xs: 'visible', md: 'hidden' }, bgcolor: '#fff' }}>

      {/* ══ HEADER (sticky) ══════════════════════════════════════════════ */}
      <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 200, bgcolor: '#fff', borderBottom: { xs: 'none', md: '1px solid #f0f0f0' }, flexShrink: 0 }}>

        {/* ── Ligne 1 : logo + auth ─────────────────────────── */}
        <Box sx={{
          px: { xs: 2, sm: 2.5, md: 3 },
          height: { xs: 52, md: 64 },
          display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 },
          borderBottom: { xs: '1px solid #f0f0f0', md: 'none' },
        }}>

          {/* Hamburger mobile */}
          <IconButton onClick={() => setMobileDrawer(true)}
            sx={{ display: { xs: 'flex', md: 'none' }, color: '#1a1a1a', p: 0.8 }}>
            <MenuIcon sx={{ fontSize: 24 }} />
          </IconButton>

          {/* Logo — centré mobile, gauche desktop */}
          <Typography component={Link} to="/"
            sx={{ fontWeight: 900, color: '#1a1a1a', textDecoration: 'none', letterSpacing: '0.05em',
              fontSize: { xs: '1.05rem', md: '1.15rem' },
              flex: { xs: 1, md: 0 }, textAlign: { xs: 'center', md: 'left' },
              '&:hover': { opacity: 0.75 } }}>
            USAFIRI
          </Typography>

          {/* Desktop : toggle + location + search */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2, flex: 1 }}>
            {/* Toggle */}
            <Box sx={{ display: 'flex', bgcolor: '#f5f5f5', borderRadius: '24px', p: '3px', flexShrink: 0 }}>
              {[{ key: 'delivery', label: 'Livraison' }, { key: 'pickup', label: 'À emporter' }].map(opt => (
                <Box key={opt.key} onClick={() => setOrderType(opt.key)} sx={{
                  px: 2, py: 0.6, borderRadius: '20px', cursor: 'pointer',
                  bgcolor: orderType === opt.key ? '#fff' : 'transparent',
                  boxShadow: orderType === opt.key ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                  transition: 'all 0.18s',
                }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
                    {opt.label}
                  </Typography>
                </Box>
              ))}
            </Box>
            {/* Location */}
            <Box onClick={() => { setLocationOpen(true); setLocationQuery(''); setLocationSuggestions([]); }}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.6, cursor: 'pointer', px: 1.5, py: 0.7, borderRadius: '24px', border: '1px solid #e0e0e0', flexShrink: 0, '&:hover': { bgcolor: '#f9f9f9' } }}>
              <LocationOnIcon sx={{ fontSize: 16, color: '#1a1a1a' }} />
              <Typography sx={{ fontSize: '0.83rem', fontWeight: 600, color: '#1a1a1a', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {locationLabel}
              </Typography>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#bbb' }} />
              <Typography sx={{ fontSize: '0.83rem', fontWeight: 600, color: '#1a1a1a' }}>{deliveryTime}</Typography>
              <ArrowForwardIosIcon sx={{ fontSize: 10, color: '#888', ml: 0.3 }} />
            </Box>
            {/* Search */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f5f5f5', borderRadius: '10px', px: 1.5, py: 0.75, maxWidth: 380 }}>
              <SearchIcon sx={{ color: '#6b6b6b', fontSize: 20, flexShrink: 0 }} />
              <InputBase fullWidth placeholder="Rechercher dans Usafiri" value={search}
                onChange={e => setSearch(e.target.value)}
                sx={{ fontSize: '0.88rem', color: '#1a1a1a', '& input::placeholder': { color: '#9b9b9b' } }} />
              {search && <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.2, color: '#9b9b9b' }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>}
            </Box>
          </Box>

          {/* Auth */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: { xs: 0, md: 'auto' } }}>
            {isAuthenticated ? (
              <>
                <IconButton onClick={e => setAuthMenu(e.currentTarget)} sx={{ p: 0 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: '#552583', fontSize: '0.72rem', fontWeight: 700 }}>
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </Avatar>
                </IconButton>
                <Menu anchorEl={authMenu} open={Boolean(authMenu)} onClose={() => setAuthMenu(null)}
                  PaperProps={{ sx: { mt: 1, minWidth: 180, borderRadius: '12px', border: '1px solid #f0f0f0', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' } }}>
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.84rem' }}>{user?.first_name} {user?.last_name}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>{user?.email}</Typography>
                  </Box>
                  <MenuItem onClick={() => { navigate('/dashboard'); setAuthMenu(null); }} sx={{ fontSize: '0.84rem', gap: 1.5, py: 1.1 }}>
                    <DashboardIcon sx={{ fontSize: 17, color: '#888' }} /> Tableau de bord
                  </MenuItem>
                  <MenuItem onClick={() => { navigate('/profile'); setAuthMenu(null); }} sx={{ fontSize: '0.84rem', gap: 1.5, py: 1.1 }}>
                    <PersonIcon sx={{ fontSize: 17, color: '#888' }} /> Mon profil
                  </MenuItem>
                  <MenuItem onClick={() => { navigate('/restaurant/orders'); setAuthMenu(null); }} sx={{ fontSize: '0.84rem', gap: 1.5, py: 1.1 }}>
                    <ReceiptLongIcon sx={{ fontSize: 17, color: '#888' }} /> Mes commandes
                  </MenuItem>
                  <MenuItem onClick={() => { logout(); setAuthMenu(null); navigate('/'); }} sx={{ fontSize: '0.84rem', gap: 1.5, py: 1.1, color: '#b91c1c' }}>
                    <LogoutIcon sx={{ fontSize: 17 }} /> Déconnexion
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button component={Link} to="/login" size="small"
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', color: '#1a1a1a', border: '1px solid #e0e0e0', borderRadius: '24px', px: 2, py: 0.6, '&:hover': { bgcolor: '#f5f5f5' } }}>
                  Connexion
                </Button>
                <Button component={Link} to="/register" size="small"
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', bgcolor: '#1a1a1a', color: '#fff', borderRadius: '24px', px: 2, py: 0.6, '&:hover': { bgcolor: '#333' }, display: { xs: 'none', sm: 'flex' } }}>
                  Inscription
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {/* ── Ligne 2 mobile : toggle + adresse + heure ────── */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, px: 2, pt: 1.2, pb: 0.5 }}>
          {/* Toggle */}
          <Box sx={{ display: 'flex', bgcolor: '#f5f5f5', borderRadius: '24px', p: '3px', mb: 1.2, alignSelf: 'flex-start', width: 'fit-content' }}>
            {[{ key: 'delivery', label: 'Livraison' }, { key: 'pickup', label: 'À emporter' }].map(opt => (
              <Box key={opt.key} onClick={() => setOrderType(opt.key)} sx={{
                px: 1.8, py: 0.5, borderRadius: '20px', cursor: 'pointer',
                bgcolor: orderType === opt.key ? '#fff' : 'transparent',
                boxShadow: orderType === opt.key ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                transition: 'all 0.18s',
              }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a1a' }}>{opt.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* Adresse + heure — gros, cliquable */}
          <Box onClick={() => { setLocationOpen(true); setLocationQuery(''); setLocationSuggestions([]); }}
            sx={{ cursor: 'pointer', mb: 1.2 }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#888', fontWeight: 500, mb: 0.1 }}>
              {deliveryTime === 'Maintenant' ? 'Livrer maintenant' : 'Livraison planifiée'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
                {locationLabel}
              </Typography>
              <ArrowForwardIosIcon sx={{ fontSize: 13, color: '#1a1a1a', transform: 'rotate(90deg)' }} />
            </Box>
          </Box>

          {/* Search bar mobile — pleine largeur */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f5f5f5', borderRadius: '12px', px: 1.5, py: 0.9, mb: 0.5 }}>
            <SearchIcon sx={{ color: '#6b6b6b', fontSize: 20, flexShrink: 0 }} />
            <InputBase fullWidth placeholder="Rechercher dans Usafiri" value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ fontSize: '0.9rem', color: '#1a1a1a', '& input::placeholder': { color: '#9b9b9b' } }} />
            {search && <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.2, color: '#9b9b9b' }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>}
          </Box>
        </Box>
      </Box>
      {/* ── Drawer mobile — même contenu que la nav principale ─── */}
      <Drawer anchor="left" open={mobileDrawer} onClose={() => setMobileDrawer(false)}
        PaperProps={{ sx: { width: 280, bgcolor: '#fff', height: '100%', display: 'flex', flexDirection: 'column' } }}>

        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #ebebeb' }}>
          <Typography sx={{ fontWeight: 900, color: '#1a1a1a', letterSpacing: '0.1em', fontSize: '1.05rem' }}>USAFIRI</Typography>
          <IconButton onClick={() => setMobileDrawer(false)} sx={{ color: '#8a8a8a', p: 0.5 }}>
            <CloseIcon sx={{ fontSize: '1.2rem' }} />
          </IconButton>
        </Box>

        {/* Profil utilisateur */}
        {isAuthenticated && (
          <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #ebebeb' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: '#552583', fontSize: '0.82rem', fontWeight: 700 }}>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </Avatar>
              <Box>
                <Typography sx={{ color: '#1a1a1a', fontWeight: 700, fontSize: '0.88rem', lineHeight: 1.2 }}>
                  {user?.first_name} {user?.last_name}
                </Typography>
                <Typography sx={{ color: '#8a8a8a', fontSize: '0.72rem' }}>{user?.email}</Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Nav items */}
        <List sx={{ flex: 1, px: 1.5, pt: 1.5, overflowY: 'auto' }}>
          {[
            { text: 'Restaurants', path: '/restaurants', icon: <RestaurantIcon /> },
            { text: 'Logistique',  path: '/logistics',   icon: <LocalShippingIcon />, soon: true },
            { text: 'VTC',         path: '/vtc',         icon: <DirectionsCarIcon />, soon: true },
            { text: 'Course',      path: '/courses',     icon: <TwoWheelerIcon />,    soon: true },
            { text: 'Covoiturage', path: '/carpool',     icon: <GroupIcon />,         soon: true },
            { text: 'Location',    path: '/car-rental',  icon: <TimeToLeaveIcon />,   soon: true },
          ].map(item => (
            <ListItem key={item.text} onClick={() => { if (!item.soon) { navigate(item.path); setMobileDrawer(false); } }}
              sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, opacity: item.soon ? 0.35 : 1, cursor: item.soon ? 'default' : 'pointer', '&:hover': { bgcolor: item.soon ? 'transparent' : '#f6f6f6' } }}>
              <ListItemIcon sx={{ color: '#4a4a4a', minWidth: 36, '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: '0.88rem', fontWeight: 500, color: '#1a1a1a' }}>{item.text}</span>
                  {item.soon && <Box component="span" sx={{ bgcolor: '#f6f6f6', color: '#8a8a8a', fontSize: '0.56rem', fontWeight: 700, px: 0.7, py: 0.15, borderRadius: '3px', letterSpacing: '0.05em' }}>BIENTÔT</Box>}
                </Box>
              } />
            </ListItem>
          ))}

          <ListItem onClick={() => { navigate('/partner/register'); setMobileDrawer(false); }}
            sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#f6f6f6' } }}>
            <ListItemIcon sx={{ color: '#552583', minWidth: 36, '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}>
              <BusinessCenterIcon />
            </ListItemIcon>
            <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#552583' }}>Devenir Partenaire</span>} />
          </ListItem>

          {isAuthenticated && (
            <>
              <Divider sx={{ my: 1.5, borderColor: '#ebebeb' }} />
              <ListItem onClick={() => { navigate('/dashboard'); setMobileDrawer(false); }}
                sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#f6f6f6' } }}>
                <ListItemIcon sx={{ color: '#4a4a4a', minWidth: 36, '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}><DashboardIcon /></ListItemIcon>
                <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 500, color: '#1a1a1a' }}>Tableau de bord</span>} />
              </ListItem>
              <ListItem onClick={() => { navigate('/profile'); setMobileDrawer(false); }}
                sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#f6f6f6' } }}>
                <ListItemIcon sx={{ color: '#4a4a4a', minWidth: 36, '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}><PersonIcon /></ListItemIcon>
                <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 500, color: '#1a1a1a' }}>Mon profil</span>} />
              </ListItem>
              <ListItem onClick={() => { navigate('/restaurant/orders'); setMobileDrawer(false); }}
                sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#f6f6f6' } }}>
                <ListItemIcon sx={{ color: '#4a4a4a', minWidth: 36, '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}><ReceiptLongIcon /></ListItemIcon>
                <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 500, color: '#1a1a1a' }}>Mes commandes</span>} />
              </ListItem>
              {user?.user_type === 'restaurant_owner' && (
                <ListItem onClick={() => { navigate('/partner/restaurant'); setMobileDrawer(false); }}
                  sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#f6f6f6' } }}>
                  <ListItemIcon sx={{ color: '#4a4a4a', minWidth: 36, '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}><StorefrontIcon /></ListItemIcon>
                  <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 500, color: '#1a1a1a' }}>Mon restaurant</span>} />
                </ListItem>
              )}
              {user?.user_type === 'delivery' && (
                <ListItem onClick={() => { navigate('/partner/delivery'); setMobileDrawer(false); }}
                  sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#f6f6f6' } }}>
                  <ListItemIcon sx={{ color: '#4a4a4a', minWidth: 36, '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}><DeliveryDiningIcon /></ListItemIcon>
                  <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 500, color: '#1a1a1a' }}>Mode livreur</span>} />
                </ListItem>
              )}
              {user?.user_type === 'admin' && (
                <ListItem onClick={() => { navigate('/admin/dashboard'); setMobileDrawer(false); }}
                  sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#f6f6f6' } }}>
                  <ListItemIcon sx={{ color: '#4a4a4a', minWidth: 36, '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}><AdminPanelSettingsIcon /></ListItemIcon>
                  <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 500, color: '#1a1a1a' }}>Administration</span>} />
                </ListItem>
              )}
            </>
          )}
        </List>

        {/* Footer */}
        <Box sx={{ px: 2, py: 2, borderTop: '1px solid #ebebeb' }}>
          {isAuthenticated ? (
            <Button fullWidth onClick={() => { logout(); setMobileDrawer(false); navigate('/'); }}
              sx={{ color: '#4a4a4a', borderRadius: '10px', py: 1, border: '1px solid #ebebeb', textTransform: 'none', fontWeight: 500, fontSize: '0.85rem', '&:hover': { bgcolor: '#f6f6f6' } }}>
              Déconnexion
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button fullWidth onClick={() => { navigate('/login'); setMobileDrawer(false); }}
                sx={{ color: '#1a1a1a', borderRadius: '10px', py: 0.9, border: '1px solid #ebebeb', textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', '&:hover': { bgcolor: '#f6f6f6' } }}>
                Connexion
              </Button>
              <Button fullWidth onClick={() => { navigate('/register'); setMobileDrawer(false); }}
                sx={{ bgcolor: '#1a1a1a', color: 'white', borderRadius: '10px', py: 0.9, fontWeight: 700, fontSize: '0.85rem', textTransform: 'none', '&:hover': { bgcolor: '#4a4a4a' } }}>
                Inscription
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* ── CARTE MOBILE (top, xs uniquement, pickup seulement) ─────────── */}
      <Box sx={{ display: { xs: orderType === 'pickup' ? 'block' : 'none', md: 'none' }, height: { xs: 'calc(50vh - 64px)', sm: 'calc(45vh - 64px)' }, flexShrink: 0, position: 'relative' }}>
        {mapsLoaded ? (
          <>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={MAYOTTE} zoom={12} options={mapOptions} onLoad={onMapLoad}
          >
            {restaurants.map(r => {
              const coords = getCoords(r);
              if (!coords) return null;
              return (
                <OverlayView key={r.id} position={coords}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h / 2 })}
                >
                  <RestaurantMarker
                    r={r} hovered={hoveredId === r.id}
                    onEnter={() => setHoveredId(r.id)}
                    onLeave={() => setHoveredId(null)}
                    onClick={() => navigate(`/restaurants/${r.slug || r.id}`)}
                  />
                </OverlayView>
              );
            })}
          </GoogleMap>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: '#f0f0f0' }}>
            <CircularProgress size={24} sx={{ color: '#1a1a1a' }} />
          </Box>
        )}
      </Box>

      {/* ── CONTENU PRINCIPAL (liste + carte desktop pickup) ────────────── */}
      <Box sx={{ flex: 1, display: 'flex', overflow: { xs: 'visible', md: 'hidden' } }}>

      {/* ── PANEL LISTE ─────────────────────────────────────────────────── */}
      <Box sx={{
        width: { xs: '100%', md: orderType === 'pickup' ? (listCollapsed ? 0 : '55%') : '100%' },
        display: 'flex', flexDirection: 'column',
        borderRight: orderType === 'pickup' && !listCollapsed ? '1px solid #f0f0f0' : 'none',
        overflow: { xs: 'visible', md: 'hidden' }, transition: 'width 0.3s ease',
        flexShrink: 0,
      }}>

        {/* ── Sticky sub-header : location (mobile) + catégories + filtres ── */}
        <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 1, md: 1.5 }, pb: 0, bgcolor: 'white', flexShrink: 0 }}>

          <Box>

            {/* Location chip */}
            <Box
              onClick={() => { setLocationOpen(true); setLocationQuery(''); setLocationSuggestions([]); }}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', cursor: 'pointer', color: '#6b6b6b', borderRadius: '8px', px: 1, py: 0.5, '&:hover': { color: '#1a1a1a', bgcolor: 'rgba(0,0,0,0.04)' } }}
            >
              <LocationOnIcon sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{locationLabel}</Typography>
              <ArrowForwardIosIcon sx={{ fontSize: 10 }} />
            </Box>

            {/* Dialog livraison style Uber Eats */}
            <Dialog
              open={locationOpen}
              onClose={() => { setLocationOpen(false); setLocationMode('main'); setLocationQuery(''); setLocationSuggestions([]); }}
              fullWidth maxWidth="xs"
              PaperProps={{ sx: { borderRadius: '20px', m: 2, p: 0, overflow: 'hidden' } }}
            >
              <DialogContent sx={{ p: 0 }}>

                {/* ── MODE PRINCIPAL ── */}
                {locationMode === 'main' && (
                  <Box sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                      <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: '#1a1a1a' }}>
                        Détails de la livraison
                      </Typography>
                      <IconButton size="small" onClick={() => { setLocationOpen(false); setLocationMode('main'); }} sx={{ bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#eee' } }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>

                    {/* Adresse */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, borderBottom: '1px solid #f0f0f0' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
                        <LocationOnIcon sx={{ color: '#1a1a1a', fontSize: 22, mt: 0.1, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {locationLabel}
                          </Typography>
                          {locationSubLabel && locationSubLabel !== locationLabel && (
                            <Typography sx={{ fontSize: '0.8rem', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {locationSubLabel}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Button
                        onClick={() => { setLocationMode('edit'); setLocationQuery(''); setLocationSuggestions([]); }}
                        sx={{ ml: 1, flexShrink: 0, border: '1px solid #e0e0e0', borderRadius: '20px', color: '#1a1a1a', fontWeight: 600, fontSize: '0.82rem', textTransform: 'none', px: 2, py: 0.6, '&:hover': { bgcolor: '#f5f5f5' } }}
                      >
                        Modifier
                      </Button>
                    </Box>

                    {/* Heure de livraison */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ScheduleIcon sx={{ color: '#1a1a1a', fontSize: 22 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a' }}>
                          {deliveryTime}
                        </Typography>
                      </Box>
                      <Button
                        onClick={() => setDeliveryTime(t => t === 'Maintenant' ? 'Planifier' : 'Maintenant')}
                        sx={{ border: '1px solid #e0e0e0', borderRadius: '20px', color: '#1a1a1a', fontWeight: 600, fontSize: '0.82rem', textTransform: 'none', px: 2, py: 0.6, '&:hover': { bgcolor: '#f5f5f5' } }}
                      >
                        Planifier
                      </Button>
                    </Box>

                    {/* Bouton Terminé */}
                    <Button
                      fullWidth
                      onClick={() => { setLocationOpen(false); setLocationMode('main'); }}
                      sx={{ mt: 1, bgcolor: '#1a1a1a', color: 'white', borderRadius: '12px', py: 1.6, fontWeight: 700, fontSize: '1rem', textTransform: 'none', '&:hover': { bgcolor: '#333' } }}
                    >
                      Terminé
                    </Button>
                  </Box>
                )}

                {/* ── MODE RECHERCHE ── */}
                {locationMode === 'edit' && (
                  <Box>
                    {/* Input */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                      <IconButton size="small" onClick={() => setLocationMode('main')} sx={{ color: '#1a1a1a' }}>
                        <ArrowForwardIosIcon sx={{ fontSize: 16, transform: 'rotate(180deg)' }} />
                      </IconButton>
                      <InputBase
                        autoFocus fullWidth
                        placeholder="Entrez votre adresse..."
                        value={locationQuery}
                        onChange={handleLocationInput}
                        sx={{ fontSize: '0.95rem', color: '#1a1a1a' }}
                      />
                      {locationLoading
                        ? <CircularProgress size={18} sx={{ color: '#1a1a1a', flexShrink: 0 }} />
                        : locationQuery && <IconButton size="small" onClick={() => { setLocationQuery(''); setLocationSuggestions([]); }} sx={{ color: '#999' }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                      }
                    </Box>

                    {/* Utiliser ma position */}
                    <Box onClick={handleGeolocate} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, cursor: 'pointer', borderBottom: '1px solid #f0f0f0', '&:hover': { bgcolor: '#fafafa' } }}>
                      {geoLoading ? <CircularProgress size={18} sx={{ color: '#1a1a1a' }} /> : <MyLocationIcon sx={{ color: '#1a1a1a', fontSize: 20 }} />}
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#1a1a1a' }}>Utiliser ma position</Typography>
                    </Box>

                    {/* Suggestions */}
                    {locationSuggestions.map((pred) => (
                      <Box key={pred.place_id} onClick={() => handleLocationSelect(pred)} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2, py: 1.5, cursor: 'pointer', borderBottom: '1px solid #f8f8f8', '&:hover': { bgcolor: '#fafafa' } }}>
                        <LocationOnIcon sx={{ color: '#888', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#1a1a1a' }}>
                            {pred.structured_formatting?.main_text || pred.description}
                          </Typography>
                          {pred.structured_formatting?.secondary_text && (
                            <Typography sx={{ fontSize: '0.78rem', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {pred.structured_formatting.secondary_text}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}

                    {locationSuggestions.length === 0 && !locationLoading && locationQuery.length > 1 && (
                      <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.88rem', color: '#888' }}>Aucun résultat trouvé</Typography>
                      </Box>
                    )}

                    {locationQuery.length === 0 && (
                      <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.85rem', color: '#bbb' }}>Ex : 15 rue de la Paix, Mamoudzou</Typography>
                      </Box>
                    )}

                    {locationSuggestions.length > 0 && (
                      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <img src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png" alt="Google" style={{ height: 14 }} />
                      </Box>
                    )}
                  </Box>
                )}

              </DialogContent>
            </Dialog>
          </Box>


          {/* Category icons */}
          <Box sx={{ position: 'relative', mb: { xs: 1, md: 2 }, mx: { xs: -2, md: -3 }, display: courses ? 'none' : 'block' }}>
            {/* Flèches — desktop uniquement */}
            <Box onClick={() => catScrollRef.current?.scrollBy({ left: -220, behavior: 'smooth' })}
              sx={{ display: { xs: 'none', md: 'flex' }, position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
                zIndex: 2, width: 32, height: 32, bgcolor: '#fff', borderRadius: '50%',
                alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)', border: '1px solid #ebebeb', fontSize: 14,
                '&:hover': { bgcolor: '#f5f5f5' } }}>‹</Box>
            <Box onClick={() => catScrollRef.current?.scrollBy({ left: 220, behavior: 'smooth' })}
              sx={{ display: { xs: 'none', md: 'flex' }, position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
                zIndex: 2, width: 32, height: 32, bgcolor: '#fff', borderRadius: '50%',
                alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)', border: '1px solid #ebebeb', fontSize: 14,
                '&:hover': { bgcolor: '#f5f5f5' } }}>›</Box>
            <Box ref={catScrollRef} sx={{
              display: 'flex', gap: 0,
              overflowX: 'auto', px: { xs: 2, md: 3 },
              '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none',
            }}>
            {CATEGORIES.map(cat => (
              <Box
                key={cat.value}
                onClick={() => setSelectedCuisine(p => p === cat.value ? null : cat.value)}
                sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3,
                  minWidth: { xs: 58, md: 72 }, cursor: 'pointer', px: { xs: 0.5, md: 1 }, py: 0.5, flexShrink: 0,
                  borderBottom: selectedCuisine === cat.value ? '2px solid #1a1a1a' : '2px solid transparent',
                  transition: 'all 0.15s',
                  opacity: selectedCuisine && selectedCuisine !== cat.value ? 0.45 : 1,
                }}
              >
                <Box sx={{
                  width: { xs: 44, md: 60 }, height: { xs: 44, md: 60 }, borderRadius: '50%', bgcolor: '#f5f5f5',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: { xs: 20, md: 26 },
                  transition: 'transform 0.15s',
                  '&:hover': { transform: 'scale(1.08)' },
                }}>
                  {cat.icon}
                </Box>
                <Typography sx={{ fontSize: { xs: '0.62rem', md: '0.7rem' }, fontWeight: 600, color: '#1a1a1a', textAlign: 'center', lineHeight: 1.2 }}>
                  {cat.label}
                </Typography>
              </Box>
            ))}
            </Box>
          </Box>

          {/* Filter chips row */}
          <Box sx={{
            display: 'flex', gap: 1, pb: 1.5,
            overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none',
            borderBottom: '1px solid #f0f0f0',
          }}>
            {/* Prix */}
            <Chip
              icon={<TuneIcon sx={{ fontSize: '14px !important' }} />}
              label="Prix"
              size="small"
              onClick={() => setSortBy(p => p === 'price' ? null : 'price')}
              sx={{
                fontWeight: 600, fontSize: '0.78rem', borderRadius: '20px',
                bgcolor: sortBy === 'price' ? '#1a1a1a' : 'white',
                color: sortBy === 'price' ? 'white' : '#1a1a1a',
                border: '1px solid #e0e0e0',
                '&:hover': { bgcolor: sortBy === 'price' ? '#333' : '#f5f5f5' },
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
            {/* Les mieux notés */}
            <Chip
              icon={<StarIcon sx={{ fontSize: '14px !important' }} />}
              label="Les mieux notés"
              size="small"
              onClick={() => setSortBy(p => p === 'rating' ? null : 'rating')}
              sx={{
                fontWeight: 600, fontSize: '0.78rem', borderRadius: '20px',
                bgcolor: sortBy === 'rating' ? '#1a1a1a' : 'white',
                color: sortBy === 'rating' ? 'white' : '#1a1a1a',
                border: '1px solid #e0e0e0',
                '&:hover': { bgcolor: sortBy === 'rating' ? '#333' : '#f5f5f5' },
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
            {/* Trier */}
            <Chip
              label={`${topSortLabel} ▾`}
              size="small"
              onClick={() => setSortBy(p => p === 'time' ? null : 'time')}
              sx={{
                fontWeight: 600, fontSize: '0.78rem', borderRadius: '20px',
                bgcolor: sortBy === 'time' ? '#1a1a1a' : 'white',
                color: sortBy === 'time' ? 'white' : '#1a1a1a',
                border: '1px solid #e0e0e0',
                '&:hover': { bgcolor: sortBy === 'time' ? '#333' : '#f5f5f5' },
              }}
            />
          </Box>
        </Box>

        {/* ── Scrollable list ── */}
        <Box sx={{ flex: 1, overflowY: { xs: 'visible', md: 'auto' }, px: { xs: 2, md: 3 }, pt: 2.5, pb: 4 }}>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
              <CircularProgress size={28} sx={{ color: '#1a1a1a' }} />
            </Box>
          ) : filtered.length === 0 && courses && !search ? (
            <Box sx={{ textAlign: 'center', pt: 8, px: 2 }}>
              <Typography sx={{ fontSize: 40, mb: 2 }}>🛒</Typography>
              <Typography sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>Les épiceries arrivent bientôt</Typography>
              <Typography sx={{ fontSize: '0.84rem', color: '#6b6b6b', mb: 3, maxWidth: 420, mx: 'auto' }}>
                Bientôt, faites vos courses dans les épiceries et doukas de Mayotte, livrées chez vous comme vos repas.
              </Typography>
              <Button component={Link} to="/partner/register?type=store"
                sx={{ textTransform: 'none', fontWeight: 700, color: '#fff', bgcolor: '#1a1a1a', borderRadius: '10px', px: 3, '&:hover': { bgcolor: '#333' } }}>
                Vous tenez une épicerie ? Vendez sur Usafiri
              </Button>
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ textAlign: 'center', pt: 8 }}>
              <Typography sx={{ fontSize: 40, mb: 2 }}>🍽️</Typography>
              <Typography sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>Aucun restaurant trouvé</Typography>
              <Typography sx={{ fontSize: '0.84rem', color: '#9b9b9b', mb: 3 }}>Essayez un autre filtre ou une autre recherche</Typography>
              <Button onClick={() => { setSearch(''); setSelectedCuisine(null); setSortBy(null); }}
                sx={{ textTransform: 'none', fontWeight: 700, color: '#1a1a1a', textDecoration: 'underline' }}>
                Réinitialiser
              </Button>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontSize: '0.78rem', color: '#6b6b6b', mb: 2 }}>
                {filtered.length} {courses ? 'commerce' : 'restaurant'}{filtered.length > 1 ? 's' : ''}
              </Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 2.5,
              }}>
                {filtered.map(r => (
                  <RestaurantCard key={r.id} r={r} fee={deliveryPoint ? estimateDeliveryFee(r, deliveryPoint) : null} />
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* ── CARTE DESKTOP (pickup uniquement) ───────────────────────────── */}
      {orderType === 'pickup' && (
        <Box sx={{ display: { xs: 'none', md: 'block' }, flex: 1, position: 'relative' }}>
          {mapsLoaded ? (
            <>
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={MAYOTTE} zoom={12} options={mapOptions} onLoad={onMapLoad}
              onDragEnd={() => { setMapMoved(true); }}
              onZoomChanged={() => { if (mapInstanceRef.current) setMapMoved(true); }}
            >
              {restaurants.map(r => {
                const coords = getCoords(r);
                if (!coords) return null;
                return (
                  <OverlayView key={r.id} position={coords}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h / 2 })}
                  >
                    <RestaurantMarker
                      r={r} hovered={hoveredId === r.id}
                      onEnter={() => setHoveredId(r.id)}
                      onLeave={() => setHoveredId(null)}
                      onClick={() => navigate(`/restaurants/${r.slug || r.id}`)}
                    />
                  </OverlayView>
                );
              })}
            </GoogleMap>

            {/* Bouton Rechercher ici */}
            {mapMoved && (
              <Box sx={{ position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
                <Box onClick={() => {
                    const m = mapInstanceRef.current;
                    if (!m) return;
                    const c = m.getCenter();
                    setSearchCenter({ lat: c.lat(), lng: c.lng() });
                    setMapMoved(false);
                  }}
                  sx={{
                    bgcolor: '#fff', borderRadius: '20px', px: 2.5, py: 1,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.22)', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 1,
                    border: '1px solid rgba(0,0,0,0.08)', whiteSpace: 'nowrap',
                    fontSize: '0.85rem', fontWeight: 700, color: '#1a1a1a',
                    transition: 'box-shadow 0.15s',
                    '&:hover': { boxShadow: '0 4px 18px rgba(0,0,0,0.28)' },
                  }}>
                  <SearchIcon sx={{ fontSize: 16 }} />
                  Rechercher ici
                </Box>
              </Box>
            )}

            {/* Bouton collapse liste */}
            <Box onClick={() => setListCollapsed(v => !v)}
              sx={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                zIndex: 10, width: 32, height: 32, borderRadius: '50%',
                bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.22)',
                border: '1px solid rgba(0,0,0,0.08)', fontSize: 16, color: '#1a1a1a',
                transition: 'background 0.15s',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}>
              {listCollapsed ? '›' : '‹'}
            </Box>

            {/* Boutons zoom */}
            <Box sx={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              zIndex: 10, borderRadius: '8px', overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.22)', border: '1px solid rgba(0,0,0,0.08)',
            }}>
              {[{ label: '+', delta: 1 }, { label: '−', delta: -1 }].map(({ label, delta }, i) => (
                <React.Fragment key={label}>
                  {i === 1 && <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.1)' }} />}
                  <Box onClick={() => { const m = mapInstanceRef.current; if (m) m.setZoom(m.getZoom() + delta); }}
                    sx={{
                      width: 36, height: 36, bgcolor: '#fff', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', cursor: 'pointer', fontSize: 20, fontWeight: 300,
                      color: '#1a1a1a', userSelect: 'none',
                      '&:hover': { bgcolor: '#f5f5f5' }, '&:active': { bgcolor: '#ebebeb' },
                    }}>
                    {label}
                  </Box>
                </React.Fragment>
              ))}
            </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: '#f5f5f5' }}>
              <CircularProgress size={24} sx={{ color: '#1a1a1a' }} />
            </Box>
          )}
        </Box>
      )}

      </Box> {/* fin contenu principal */}
    </Box>
  );
}
